import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { getDiscoveriesBySlugs, type JourneyItem } from "@/lib/content.functions";
import { journeyByKey } from "@/lib/descent";
import { hydrateNodes, journeySlugs } from "@/lib/journey-hydrate";
import { DescentEngine } from "@/components/DescentEngine";
import { usePageView } from "@/hooks/use-page-view";

export const Route = createFileRoute("/journeys/$key")({
  beforeLoad: ({ params }) => {
    // Ocean keeps its original public URL.
    if (params.key === "ocean") throw redirect({ to: "/descent" });
  },
  loader: async ({ context, params }) => {
    const config = journeyByKey(params.key);
    if (!config) throw notFound();
    const items = await context.queryClient.ensureQueryData({
      queryKey: ["journey-items", config.key],
      queryFn: () => getDiscoveriesBySlugs({ data: { slugs: journeySlugs(config) } }),
    });
    return { key: params.key, items: items as JourneyItem[] };
  },
  head: ({ params, loaderData }) => {
    const config = journeyByKey(params.key);
    if (!config) return { meta: [{ title: "Journey not found" }, { name: "robots", content: "noindex" }] };
    const url = `https://smartasaurus.com/journeys/${config.key}`;
    const title = `${config.title} - an interactive Smartasaurus journey`;
    const desc = config.intro.slice(0, 155);
    const img = loaderData?.items?.find((i) => i.image_url)?.image_url ?? undefined;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...(img ? [{ property: "og:image", content: img }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(img ? [{ name: "twitter:image", content: img }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: JourneyPage,
});

function JourneyPage() {
  const { key, items } = Route.useLoaderData();
  usePageView(`journey-${key}`);
  const config = journeyByKey(key)!;
  return (
    <DescentEngine
      config={config}
      nodes={hydrateNodes(config, items)}
      shareUrl={`https://smartasaurus.com/journeys/${config.key}`}
    />
  );
}
